import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Paper,
  Grid,
  TextField,
  Button,
  MenuItem,
  GlobalStyles,
  Checkbox,
  ListSubheader,
  InputAdornment,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTheme, alpha } from '@mui/material/styles';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import { userService } from '../../../core/services/user.service';
import { AuditLogQuery } from '../../../core/types';
import RsuiteDateRangePickerWrapper from 'core/common/globalstyle/global-style-datepicker';
import moduleGroupsData from '../data/modules.json';
import { logAmplitudeEventWithPayload } from 'core/amplitude/amplitude';

const AMPLITUDE_APP = 'AuditLogs';

const AUDIT_EVENTS = {
  FILTER_CHANGED: 'Audit Filter Changed',
  SEARCH: 'Audit Search',
  REPORT_EXPORTED: 'Audit Report Exported',
  FILTERS_CLEARED: 'Audit Filters Cleared',
} as const;

interface ModuleChild {
  value: string;
  label: string;
}

interface ModuleGroup {
  collapsed: boolean;
  children: ModuleChild[];
}

const moduleGroups = moduleGroupsData as Record<string, ModuleGroup>;

/** Expand collapsed group labels to their child entity names for the API filter. */
function expandModuleSelections(selected: string[]): string[] {
  const expanded = new Set<string>();
  for (const item of selected) {
    const group = moduleGroups[item];
    if (group) {
      group.children.forEach(c => expanded.add(c.value));
    } else {
      expanded.add(item);
    }
  }
  return Array.from(expanded);
}

interface AuditFiltersProps {
  onFiltersChange: (filters: Partial<AuditLogQuery>) => void;
  onSearch: () => void;
  onExport: () => void;
  onClear: () => void;
  loading: boolean;
  settingsAuditMode: boolean;
  variant?: 'card' | 'inline';
}

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

// Convert the start of the user's local day to a UTC ISO string (e.g. user in
// UTC+5 selecting 04/28/2026 -> "2026-04-27T19:00:00.000Z"). This lets the
// backend filter the `datetimeoffset` audit timestamps in UTC so the rows
// returned match what the user sees displayed in their local timezone.
function startOfLocalDayUtcIso(date: Date): string {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  ).toISOString();
}

function endOfLocalDayUtcIso(date: Date): string {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  ).toISOString();
}

const AuditFilters: React.FC<AuditFiltersProps> = ({
  onFiltersChange,
  onSearch,
  onExport,
  onClear,
  loading,
  variant = 'card',
}) => {
  const theme = useTheme();
  const [searchInput, setSearchInput] = useState('');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [moduleSearchTerm, setModuleSearchTerm] = useState('');
  const moduleSearchRef = useRef<HTMLInputElement>(null);
  const moduleSelectRef = useRef<HTMLDivElement>(null);

  const sortedModuleGroups = useMemo(() => {
    return Object.entries(moduleGroups)
      .map(([groupLabel, group]) => ({
        groupLabel,
        ...group,
        children: [...group.children].sort((a, b) =>
          a.label.localeCompare(b.label)
        ),
      }))
      .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
  }, []);

  const filteredModuleGroups = useMemo(() => {
    const query = moduleSearchTerm.trim().toLowerCase();
    if (!query) return sortedModuleGroups;

    return sortedModuleGroups
      .map(group => {
        const groupMatches = group.groupLabel.toLowerCase().includes(query);
        const matchedChildren = group.children.filter(
          child =>
            child.label.toLowerCase().includes(query) ||
            child.value.toLowerCase().includes(query)
        );

        if (groupMatches) return group;
        if (matchedChildren.length > 0)
          return { ...group, children: matchedChildren };
        return null;
      })
      .filter(Boolean) as typeof sortedModuleGroups;
  }, [sortedModuleGroups, moduleSearchTerm]);
  const [users, setUsers] = useState<{ value: string; label: string }[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('All');
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // Update parent component when filters change
    const start = dateRange.startDate
      ? startOfLocalDayUtcIso(dateRange.startDate)
      : undefined;
    const end = dateRange.endDate
      ? endOfLocalDayUtcIso(dateRange.endDate)
      : undefined;
    onFiltersChange({
      search: searchInput || undefined,
      module:
        selectedModules.length > 0
          ? expandModuleSelections(selectedModules).join(',')
          : undefined,
      userId:
        selectedUser === 'All'
          ? undefined
          : selectedUser === 'System'
          ? '0'
          : selectedUser || undefined,
      startDate: start,
      endDate: end,
    });
  }, [searchInput, selectedModules, selectedUser, dateRange]);

  const loadUsers = async () => {
    try {
      const userList = await userService.getAllUsers();
      // Add 'All' and 'System' options
      const allUsers = [
        { value: 'All', label: 'All' },
        { value: 'System', label: 'System' },
        ...userList,
      ];
      // Sort users alphabetically by label, keeping 'All' at the top
      const sortedUsers = allUsers.sort((a, b) => {
        // Keep 'All' at the top
        if (a.value === 'All') return -1;
        if (b.value === 'All') return 1;
        // Sort the rest alphabetically by label (including 'System')
        return a.label.localeCompare(b.label);
      });
      setUsers(sortedUsers);
    } catch (err) {
      // Fallback to just 'All' and 'System'
      setUsers([
        { value: 'All', label: 'All' },
        { value: 'System', label: 'System' },
      ]);
    }
  };

  const trackFilterChange = useCallback(
    (filterType: string, value?: string | number | boolean) => {
      logAmplitudeEventWithPayload(AUDIT_EVENTS.FILTER_CHANGED, {
        app: AMPLITUDE_APP,
        filter_type: filterType,
        ...(value !== undefined && { value }),
      });
    },
    []
  );

  const handleDateRangeChange = (value: [Date, Date] | null) => {
    if (value && value.length === 2) {
      setDateRange({
        startDate: value[0],
        endDate: value[1],
      });
      trackFilterChange('date_range', 'custom');
    } else {
      setDateRange({
        startDate: null,
        endDate: null,
      });
      trackFilterChange('date_range', 'cleared');
    }
  };

  const handleQuickDateSet = (type: 'today' | 'last24h' | '7days') => {
    trackFilterChange('quick_date', type);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (type) {
      case 'today':
        setDateRange({
          startDate: today,
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
        });
        break;
      case 'last24h':
        setDateRange({
          startDate: new Date(now.getTime() - 24 * 60 * 60 * 1000),
          endDate: now,
        });
        break;
      case '7days':
        setDateRange({
          startDate: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
        });
        break;
    }
  };

  const buildFilterSummary = useCallback(() => {
    return {
      app: AMPLITUDE_APP,
      has_search: !!searchInput?.trim(),
      search_length: searchInput?.trim().length ?? 0,
      modules_count: selectedModules.length,
      user_filter: selectedUser,
      has_date_range: !!(dateRange.startDate && dateRange.endDate),
    };
  }, [searchInput, selectedModules, selectedUser, dateRange.startDate, dateRange.endDate]);

  const handleSearchClick = () => {
    logAmplitudeEventWithPayload(AUDIT_EVENTS.SEARCH, buildFilterSummary());
    onSearch();
  };

  const handleExportClick = () => {
    logAmplitudeEventWithPayload(AUDIT_EVENTS.REPORT_EXPORTED, buildFilterSummary());
    onExport();
  };

  const handleClearClick = () => {
    logAmplitudeEventWithPayload(AUDIT_EVENTS.FILTERS_CLEARED, { app: AMPLITUDE_APP });
    setSearchInput('');
    setSelectedModules([]);
    setSelectedUser('All');
    setDateRange({
      startDate: null,
      endDate: null,
    });
    onClear();
  };

  return (
    <Paper
      elevation={variant === 'inline' ? 0 : undefined}
      sx={{
        p: variant === 'inline' ? 0 : 1.5,
        mb: variant === 'inline' ? 0 : 1.5,
        backgroundColor: variant === 'inline' ? 'transparent' : undefined,
      }}
    >
      <RsuiteDateRangePickerWrapper />
      <GlobalStyles
        styles={theme => ({
          // Scoped input height styling for this component only
          '& .css-1aiste7-MuiInputBase-root-MuiOutlinedInput-root': {
            height: '33px',
          },
          // Theme-aware scrollbars for all containers
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : 'rgba(0, 0, 0, 0.5)',
            },
          },
          '*::-webkit-scrollbar-corner': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)',
          },
        })}
      />

      <Grid
        container
        spacing={variant === 'inline' ? 1 : 1.5}
        alignItems="center"
        sx={{
          ...(variant === 'inline' && {
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }),
        }}
      >
        <Grid item xs={12} sm={6} md={variant === 'inline' ? 2 : 2.5}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search within Record No/User/Action"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={variant === 'inline' ? 2.5 : 2}>
          <TextField
            ref={moduleSelectRef}
            select
            fullWidth
            size="small"
            label="Modules"
            sx={{
              '& .MuiInputBase-root': {
                height: '33px',
              },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                py: 0,
              },
            }}
            SelectProps={{
              multiple: true,
              renderValue: selected => {
                const sel = selected as unknown as string[];
                if (!sel || sel.length === 0) return 'All';
                if (sel.length <= 3) return sel.join(', ');
                return `${sel.length} selected`;
              },
              MenuProps: {
                autoFocus: false,
                PaperProps: {
                  style: {
                    maxHeight: 400,
                    overflow: 'auto',
                    width: moduleSelectRef.current?.clientWidth ?? 'auto',
                  },
                },
                TransitionProps: {
                  onEntered: () => {
                    moduleSearchRef.current?.focus();
                  },
                },
              },
              onClose: () => setModuleSearchTerm(''),
            }}
            value={selectedModules}
            onChange={e => {
              const value = e.target.value as string[] | string;
              let newValue: string[];
              if (typeof value === 'string') {
                newValue = value.split(',').filter(Boolean);
              } else {
                newValue = value;
              }

              // Check if a "Select all" group value was clicked (for expanded groups)
              const groupValue = newValue.find(v => v.startsWith('__group__'));
              if (groupValue) {
                const groupLabel = groupValue.replace('__group__', '');
                const group = moduleGroups[groupLabel];
                if (group && !group.collapsed) {
                  const childValues = group.children.map(c => c.value);
                  const set = new Set(
                    newValue.filter(v => !v.startsWith('__group__'))
                  );
                  const allSelected = childValues.every(v => set.has(v));
                  if (allSelected) {
                    childValues.forEach(v => set.delete(v));
                  } else {
                    childValues.forEach(v => set.add(v));
                  }
                  const finalModules = Array.from(set);
                  setSelectedModules(finalModules);
                  trackFilterChange('modules', finalModules.length);
                  return;
                }
              }

              // Filter out any special group values
              newValue = newValue.filter(v => !v.startsWith('__group__'));
              setSelectedModules(newValue);
              trackFilterChange('modules', newValue.length);
            }}
          >
            <ListSubheader
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: theme.palette.background.paper,
                pt: 1,
                pb: 1,
              }}
            >
              <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder="Search modules..."
                inputRef={moduleSearchRef}
                value={moduleSearchTerm}
                onChange={e => setModuleSearchTerm(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="small"
                        sx={{ color: theme.palette.text.secondary }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: moduleSearchTerm ? (
                    <InputAdornment position="end">
                      <ClearIcon
                        fontSize="small"
                        sx={{
                          cursor: 'pointer',
                          color: theme.palette.text.secondary,
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          setModuleSearchTerm('');
                          moduleSearchRef.current?.focus();
                        }}
                      />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </ListSubheader>

            {filteredModuleGroups.length === 0 && (
              <MenuItem disabled>
                <Box sx={{ py: 1, color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                  No modules found
                </Box>
              </MenuItem>
            )}

            {filteredModuleGroups.flatMap(group => {
              const elements: React.ReactNode[] = [];

              const groupHeaderSx = {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.25)
                    : alpha(theme.palette.primary.main, 0.12),
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.dark,
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: '32px',
                borderLeft: `3px solid ${theme.palette.primary.main}`,
              };

              if (group.collapsed) {
                elements.push(
                  <ListSubheader
                    key={`${group.groupLabel}__subheader`}
                    disableSticky
                    sx={groupHeaderSx}
                  >
                    {group.groupLabel}
                  </ListSubheader>
                );
                elements.push(
                  <MenuItem
                    key={group.groupLabel}
                    value={group.groupLabel}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedModules.includes(group.groupLabel)}
                    />
                    Select All {group.groupLabel} Settings
                  </MenuItem>
                );
              } else {
                const childValues = group.children.map(c => c.value);
                const allSelected = childValues.every(v =>
                  selectedModules.includes(v)
                );
                const someSelected =
                  !allSelected &&
                  childValues.some(v => selectedModules.includes(v));

                elements.push(
                  <ListSubheader
                    key={`${group.groupLabel}__subheader`}
                    disableSticky
                    sx={groupHeaderSx}
                  >
                    {group.groupLabel}
                  </ListSubheader>
                );
                elements.push(
                  <MenuItem
                    key={`${group.groupLabel}__all`}
                    value={`__group__${group.groupLabel}`}
                  >
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                    />
                    Select all {group.groupLabel}
                  </MenuItem>
                );
                elements.push(
                  ...group.children.map(child => (
                    <MenuItem key={child.value} value={child.value}>
                      <Checkbox
                        size="small"
                        checked={selectedModules.includes(child.value)}
                      />
                      {child.label}
                    </MenuItem>
                  ))
                );
              }

              return elements;
            })}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={variant === 'inline' ? 2 : 1.8}>
          <TextField
            select
            fullWidth
            size="small"
            label="User"
            value={selectedUser}
            onChange={e => {
              const value = e.target.value;
              setSelectedUser(value);
              trackFilterChange('user', value);
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 200, // Approximately 6-7 items
                    overflow: 'auto',
                  },
                },
              },
            }}
          >
            {users.map(u => (
              <MenuItem key={u.value} value={u.value}>
                {u.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={variant === 'inline' ? 2.5 : 2}>
          <DateRangePicker
            key={`${dateRange.startDate}-${dateRange.endDate}`}
            placeholder="Select Date Range"
            value={
              dateRange.startDate && dateRange.endDate
                ? [dateRange.startDate, dateRange.endDate]
                : null
            }
            onChange={handleDateRangeChange}
            style={{
              width: '100%',
              height: '33px',
              minWidth: variant === 'inline' ? 180 : undefined,
            }}
            format="MM/dd/yyyy"
            size="md"
            cleanable
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={variant === 'inline' ? 3 : 3.4}
          sx={{
            display: 'flex',
            gap: 0.5,
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <Button
            size="small"
            variant="text"
            sx={{ minWidth: 'auto', px: 1 }}
            onClick={() => handleQuickDateSet('today')}
          >
            Today
          </Button>
          <Button
            size="small"
            variant="text"
            sx={{ minWidth: 'auto', px: 1 }}
            onClick={() => handleQuickDateSet('last24h')}
          >
            24h
          </Button>
          <Button
            size="small"
            variant="text"
            sx={{ minWidth: 'auto', px: 1 }}
            onClick={() => handleQuickDateSet('7days')}
          >
            7d
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSearchClick}
            disabled={loading}
            sx={{ minWidth: 'auto', px: 1.5 }}
          >
            Search
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleExportClick}
            disabled={loading}
            sx={{ minWidth: 'auto', px: 1.5 }}
          >
            Export
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={handleClearClick}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AuditFilters;
